/** @format */
import {ReactNode, useCallback, useMemo, useState} from 'react';

import {Snackbar, SnackbarOrigin} from '@mui/material';

import {SnackbarBody} from './styles';

export type SnackbarVariant = 'success' | 'error' | 'warning';

export type MessageType = ReactNode | string;

type UseSnackbarOptions = {
  autoHideDuration?: number;
  anchorOrigin?: SnackbarOrigin;
  variant?: SnackbarVariant;
};

export const useSnackbar = (opts: UseSnackbarOptions = {}) => {
  const {
    autoHideDuration = 2000,
    anchorOrigin = {vertical: 'bottom', horizontal: 'center'},
    variant = 'success',
  } = opts;

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<MessageType>('');

  const showSnack = useCallback((msg: MessageType) => {
    setMessage(msg);
    setOpen(true);
  }, []);

  const closeSnack = useCallback(() => setOpen(false), []);

  const Snack = useMemo(
    () =>
      function SnackComponent() {
        return (
          <Snackbar
            open={open}
            onClose={closeSnack}
            autoHideDuration={autoHideDuration}
            anchorOrigin={anchorOrigin}
          >
            <SnackbarBody variant={variant}>{message}</SnackbarBody>
          </Snackbar>
        );
      },
    [open, message, autoHideDuration, anchorOrigin, variant, closeSnack],
  );

  return {showSnack, closeSnack, Snack};
};
